import { createBrowserRouter, type RouteObject } from "react-router";
import { LocaleLayout, RootRedirect } from "@/layouts/locale-layout";
import { AuthLayout } from "@/layouts/auth-layout";
import { StudentLayout } from "@/layouts/student-layout";
import { WithSidebarLayout } from "@/layouts/with-sidebar-layout";
import { AdminLayout } from "@/layouts/admin-layout";
import { RequireAuth } from "@/features/auth/components/route-guards";
import { RouteErrorBoundary } from "./route-error-boundary";

/**
 * Lazily loads a page module (default export) as its own chunk — a student
 * never downloads the admin CMS, and vice versa.
 */
const page = (load: () => Promise<{ default: React.ComponentType }>): Pick<RouteObject, "lazy"> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

/**
 * The URL map. Every page lives under `/:locale` (en/tr/ru/uz); the layout
 * nesting mirrors who may see what:
 *
 *   LocaleLayout ─┬─ home, auth pages, pricing (public)
 *                 ├─ StudentLayout (any signed-in user) ─┬─ WithSidebarLayout: dashboard, exams, ...
 *                 │                                      └─ full-width: results
 *                 ├─ RequireAuth only: the full-screen exam room
 *                 └─ AdminLayout (admins only): the CMS
 */
const routes: RouteObject[] = [
  { index: true, element: <RootRedirect /> },
  {
    path: ":locale",
    element: <LocaleLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, ...page(() => import("@/pages/home/home-page")) },
      { path: "legal/:slug", ...page(() => import("@/pages/legal/legal-page")) },
      { path: "contact", ...page(() => import("@/pages/contact/contact-page")) },
      // Public: signed-in students get it inside their sidebar layout, visitors see prices before signing up.
      { path: "pricing", ...page(() => import("@/pages/pricing/pricing-route")) },
      // Public: what a graded Writing/Speaking result looks like (sample answers, real AI grades).
      { path: "sample-result", ...page(() => import("@/pages/sample-result/sample-result-page")) },
      // Public: the "Shaxsiy reja" questionnaire — the whole plan before sign-up; signing in saves it.
      { path: "start", ...page(() => import("@/pages/start/start-page")) },
      // Public: a friend's Vocab Battle invite (a visitor signs in, then accepts).
      { path: "games/vocab/duel/:code", ...page(() => import("@/pages/games/vocab-duel-page")) },
      // Public: a friend's challenge link, and the short invite link printed on share cards.
      { path: "challenge/:code", ...page(() => import("@/pages/challenge/challenge-page")) },
      { path: "r/:code", ...page(() => import("@/pages/challenge/invite-redirect")) },
      {
        element: <AuthLayout />,
        children: [
          { path: "login", ...page(() => import("@/pages/auth/login-page")) },
          { path: "register", ...page(() => import("@/pages/auth/register-page")) },
          { path: "forgot-password", ...page(() => import("@/pages/auth/forgot-password-page")) },
          { path: "reset-password", ...page(() => import("@/pages/auth/reset-password-page")) },
          { path: "verify-email", ...page(() => import("@/pages/auth/verify-email-page")) },
          { path: "set-password", ...page(() => import("@/pages/auth/set-password-page")) },
        ],
      },
      {
        element: <StudentLayout />,
        children: [
          {
            element: <WithSidebarLayout />,
            children: [
              { path: "dashboard", ...page(() => import("@/pages/student/dashboard-page")) },
              { path: "exams", ...page(() => import("@/pages/student/exams-page")) },
              { path: "profile", ...page(() => import("@/pages/student/profile-page")) },
              { path: "ranking", ...page(() => import("@/pages/student/ranking-page")) },
              { path: "challenges", ...page(() => import("@/pages/student/challenges-page")) },
              { path: "tutor", ...page(() => import("@/pages/student/tutor-page")) },
              { path: "stats", ...page(() => import("@/pages/student/stats-page")) },
              { path: "streak", ...page(() => import("@/pages/student/streak-page")) },
              { path: "achievements", ...page(() => import("@/pages/student/achievements-page")) },
              { path: "plan", ...page(() => import("@/pages/student/plan-page")) },
              { path: "vocabulary", ...page(() => import("@/pages/student/vocabulary-page")) },
              // "O'yinlar": the games hub and Vocab Battle's lobby (the fight itself is full-screen, below).
              { path: "games", ...page(() => import("@/pages/games/games-page")) },
              { path: "games/vocab", ...page(() => import("@/pages/games/vocab-lobby-page")) },
            ],
          },
          { path: "attempts/:id/result", ...page(() => import("@/pages/student/attempt-result-page")) },
        ],
      },
      {
        // The exam room is full-screen: signed-in only, but without the site
        // header/sidebar of StudentLayout (it has its own top bar).
        element: <RequireAuth />,
        children: [
          { path: "exams/:id/take", ...page(() => import("@/pages/student/exam-take-page")) },
          { path: "games/vocab/battle/:id", ...page(() => import("@/pages/games/vocab-battle-page")) },
        ],
      },
      {
        path: "admin",
        element: <AdminLayout />,
        children: [
          { index: true, ...page(() => import("@/pages/admin/admin-dashboard-page")) },
          { path: "analytics", ...page(() => import("@/pages/admin/analytics-page")) },
          { path: "exams", ...page(() => import("@/pages/admin/exams-page")) },
          { path: "exams/:id", ...page(() => import("@/pages/admin/exam-builder-page")) },
          { path: "passages", ...page(() => import("@/pages/admin/passages-page")) },
          { path: "payments", ...page(() => import("@/pages/admin/payments-page")) },
          { path: "revenue", ...page(() => import("@/pages/admin/revenue-page")) },
          { path: "partner-report", ...page(() => import("@/pages/admin/partner-report-page")) },
          { path: "ai-costs", ...page(() => import("@/pages/admin/ai-costs-page")) },
          { path: "announcements", ...page(() => import("@/pages/admin/announcements-page")) },
          { path: "surveys", ...page(() => import("@/pages/admin/surveys-page")) },
          { path: "questions", ...page(() => import("@/pages/admin/questions-page")) },
          { path: "questions/:examId", ...page(() => import("@/pages/admin/exam-questions-page")) },
          { path: "settings", ...page(() => import("@/pages/admin/settings-page")) },
          { path: "legal", ...page(() => import("@/pages/admin/legal-page")) },
          { path: "contact", ...page(() => import("@/pages/admin/contact-page")) },
          { path: "subscriptions", ...page(() => import("@/pages/admin/subscriptions-page")) },
          { path: "users", ...page(() => import("@/pages/admin/users-page")) },
          { path: "attempts", ...page(() => import("@/pages/admin/attempts-page")) },
          { path: "attempts/:id", ...page(() => import("@/pages/admin/attempt-detail-page")) },
        ],
      },
      { path: "*", ...page(() => import("@/pages/not-found-page")) },
    ],
  },
];

export const router = createBrowserRouter(routes);
